'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export type StaffRole = 'admin' | 'kitchen' | 'waiter' | 'cashier';

export interface StaffProfile {
  id: string;
  auth_user_id: string;
  full_name: string;
  email: string;
  role: StaffRole;
  is_active: boolean;
  avatar_url?: string | null;
}

const AuthContext = createContext<any>({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<any>(null);
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [staffProfile, setStaffProfile] = useState<StaffProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const supabase = createClient();

  const loadStaffProfile = async (userId: string) => {
    setProfileLoading(true);
    try {
      const { data, error } = await supabase
        .from('staff_profiles')
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();
      if (!error && data) {
        setStaffProfile(data as StaffProfile);
      } else {
        setStaffProfile(null);
      }
    } catch {
      setStaffProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadStaffProfile(session.user.id);
      } else {
        setStaffProfile(null);
      }
      setLoading(false);
    });

    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadStaffProfile(session.user.id);
      } else {
        setStaffProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email: string, password: string, metadata: { fullName?: string; role?: StaffRole } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata.fullName || '',
          role: metadata.role || 'waiter',
        },
        emailRedirectTo: `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/callback`
      }
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    if (data.user) {
      // Load profile but don't block sign-in if it fails
      try {
        await loadStaffProfile(data.user.id);
      } catch {
        // Profile load failure should not prevent login
        setStaffProfile(null);
      }
    }
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setStaffProfile(null);
  };

  const getCurrentUser = async () => {
    const { data: { user }, error } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  };

  const isEmailVerified = () => user?.email_confirmed_at !== null;

  const getUserProfile = async () => {
    if (!user) return null;
    const { data, error } = await supabase
      .from('staff_profiles')
      .select('*')
      .eq('auth_user_id', user.id)
      .maybeSingle();
    if (error) throw error;
    return data;
  };

  const hasRole = (...roles: StaffRole[]): boolean => {
    if (!staffProfile) return false;
    return roles.includes(staffProfile.role);
  };

  const value = {
    user,
    session,
    loading,
    staffProfile,
    profileLoading,
    role: staffProfile?.role ?? null,
    hasRole,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
